import React, { useEffect, useState } from 'react';
import { Button, Input, Label, SingleSelect } from 'lift-ui';
import { Plus, Trash2 } from 'lucide-react';

interface InnerItem {
  id: string;
  report: string;
  hash: string;
}

interface Section {
  id: string;
  number: string;
  items: InnerItem[];
}

const DynamicNestedForm = ({
  formStateEdit,
  handleFormEditChange,
}: {
  formStateEdit: any;
  handleFormEditChange: any;
}) => {
  const reverseTransform = (objectData: any) => {
    if (Array.isArray(objectData)) {
      return objectData.map((section: any) => ({
        ...section,
        id: section.id || `section-${Date.now()}-${section.number}`,
        items: (section.items || []).map((item: any) => ({
          ...item,
          id: item.id || `item-${Date.now()}-${item.report}`
        }))
      }));
    }

    const sections = new Map();

    if (!objectData || typeof objectData !== 'object') {
      return [];
    }

    Object.entries(objectData).forEach(([key, hash]) => {
      const match = key.match(/^ds(\d+)\.(.+)$/);
      if (!match) return;

      const [, sectionNumber, report] = match;

      if (!sections.has(sectionNumber)) {
        sections.set(sectionNumber, {
          id: `section-${Date.now()}-${sectionNumber}`,
          number: sectionNumber,
          items: [],
        });
      }

      const section = sections.get(sectionNumber);
      section.items.push({
        id: `item-${Date.now()}-${report}`,
        report,
        hash,
      });
    });

    return Array.from(sections.values()).sort(
      (a, b) => parseInt(a.number) - parseInt(b.number)
    );
  };

  const [sections, setSections] = useState<Section[]>(
    formStateEdit.params ? reverseTransform(formStateEdit.params) : []
  );

  const addSection = () => {
    const newSection: Section = {
      id: `section-${Date.now()}`,
      number: '',
      items: [],
    };
    setSections([...sections, newSection]);
  };

  const addInnerItem = (sectionId: string) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: [
              ...section.items,
              {
                id: `item-${Date.now()}`,
                report: '',
                hash: '',
              },
            ],
          };
        }
        return section;
      })
    );
  };

  const updateSectionNumber = (sectionId: string, value: string) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, number: value } : section
      )
    );
  };

  const updateInnerItem = (
    sectionId: string,
    itemId: string,
    field: keyof InnerItem,
    value: string
  ) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.map((item) =>
              item.id === itemId ? { ...item, [field]: value } : item
            ),
          };
        }
        return section;
      })
    );
  };

  const removeSection = (sectionId: string) => {
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const removeInnerItem = (sectionId: string, itemId: string) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.filter((item) => item.id !== itemId),
          };
        }
        return section;
      })
    );
  };

  const hashOptions = [
    { value: 'identity', label: 'identity' },
    { value: 'report', label: 'report' },
    { value: 'brd_param', label: 'brd_param' },
    { value: 'end_date', label: 'end_date' },
    { value: 'start_date', label: 'start_date' },
    { value: 'ecpc_weight', label: 'ecpc_weight' },
    { value: 'rev_weight', label: 'rev_weight' },
    { value: 'aov_weight', label: 'aov_weight' },
    { value: 'click_weight', label: 'click_weight' },
    { value: 'cr_weight', label: 'cr_weight' },
    { value: 'newvexist_weight', label: 'newvexist_weight' },
    { value: 'conversions_weight', label: 'conversions_weight' },
    { value: 'new_user_percentage_weight', label: 'new_user_percentage_weight' },
  ];

  useEffect(() => {
    const validSections = sections.filter(section =>
      section.number && section.items && section.items.length > 0
    );

    const currentParams = JSON.stringify(formStateEdit.params || []);
    const newParams = JSON.stringify(validSections);

    if (currentParams !== newParams) {
      handleFormEditChange('params', validSections);
    }
  }, [sections, formStateEdit.params]);

  return (
    <div className="max-w-2xl mx-auto py-4">
      <div className="p-4 bg-gray-50 rounded-lg border">
        <h3 className="text-lg font-semibold text-center mb-4">
          Dynamic Form Builder
        </h3>

        <div className="flex flex-col gap-3">
          {sections.map((section) => (
            <div key={section.id} className="border rounded-lg bg-white">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="space-y-1">
                    <Label>DS Number</Label>
                    <Input
                      type="number"
                      value={section.number}
                      onChange={(e) =>
                        updateSectionNumber(section.id, e.target.value)
                      }
                      className="w-[120px]"
                      placeholder="Number"
                      min={0}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSection(section.id)}
                    className="ml-auto h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="pl-4 flex flex-col gap-2">
                  {section.items.map((item) => (
                    <div
                      key={item.id}
                      className="border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="p-3 flex flex-col gap-2">
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              removeInnerItem(section.id, item.id)
                            }
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          <Label>Param</Label>
                          <Input
                            value={item.report}
                            onChange={(e) =>
                              updateInnerItem(
                                section.id,
                                item.id,
                                'report',
                                e.target.value
                              )
                            }
                            placeholder="Enter param"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Params Type</Label>
                          <SingleSelect
                            options={hashOptions}
                            defaultValue={item.hash || ""}
                            onChange={(value) =>
                              updateInnerItem(section.id, item.id, 'hash', value)
                            }
                            disabled={formStateEdit.report_type === 'Existing'}
                            placeholder="Select params type"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addInnerItem(section.id)}
                    className="self-start"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Input Fields
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button onClick={addSection} className="w-full mt-3">
          <Plus className="h-4 w-4 mr-1" />
          Add New Section
        </Button>
      </div>
    </div>
  );
};

export default DynamicNestedForm;
